import { useState } from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { theme } from "../../lib/theme";
import { Button, Input } from "../ui";
import { useNetworkViews, useSavedViews } from "../../stores/saved-views";
import {
  activeFilterCount,
  defaultViewName,
  serializeTxFilters,
  type TxFilterState,
} from "../../lib/tx-filters";

/**
 * Shortcuts to the filter sets the user keeps coming back to, and the way to
 * add one: "Save view" snapshots the current filters under a name, and each
 * saved view opens its own focused page. When the page was opened from a view
 * ("Edit filters" there), the bar offers to write the changes back to it.
 */

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

const Views = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex-wrap: wrap;
  min-width: 0;
`;

const Heading = styled.span`
  font-size: ${theme.fontSize.xs};
  font-weight: 600;
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-right: ${theme.spacing.xs};
`;

const ViewChip = styled(Link, { shouldForwardProp: (p) => p !== "current" })<{ current?: boolean }>`
  display: inline-block;
  max-width: 260px;
  padding: 2px ${theme.spacing.sm};
  border: 1px solid ${(p) => (p.current ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.radius.md};
  background: ${(p) => (p.current ? `${theme.colors.primary}18` : "transparent")};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};

  input {
    width: 240px;
    max-width: 100%;
    padding: 4px ${theme.spacing.sm};
    font-size: ${theme.fontSize.xs};
  }
`;

const SmallButton = styled(Button)`
  padding: 4px ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
`;

const InlineLink = styled(Link)`
  color: ${theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export interface SavedViewsBarProps {
  networkId: string;
  filters: TxFilterState;
  /** The `view` URL param: set when the user came here to edit that view's filters. */
  editingViewId?: string;
}

export function SavedViewsBar({ networkId, filters, editingViewId }: SavedViewsBarProps) {
  const views = useNetworkViews(networkId);
  const save = useSavedViews((s) => s.save);
  const update = useSavedViews((s) => s.update);
  const [naming, setNaming] = useState<string | null>(null);

  const hasFilters = activeFilterCount(filters) > 0;
  const query = serializeTxFilters(filters);
  const match = hasFilters ? views.find((v) => v.query === query) : undefined;
  const editing = editingViewId ? views.find((v) => v.id === editingViewId) : undefined;
  const viewPath = (id: string) => `/${networkId}/views/${id}`;

  if (views.length === 0 && !hasFilters) return null;

  const commitSave = () => {
    const name = naming?.trim() || defaultViewName(filters);
    save(networkId, name, query);
    setNaming(null);
  };

  return (
    <Row>
      <Views>
        {views.length > 0 && <Heading>Saved views</Heading>}
        {views.map((v) => (
          <ViewChip key={v.id} to={viewPath(v.id)} current={v.id === match?.id} title={v.name}>
            {v.name}
          </ViewChip>
        ))}
      </Views>

      <Actions>
        {naming !== null ? (
          <>
            <Input
              autoFocus
              value={naming}
              placeholder="View name"
              onChange={(e) => setNaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitSave();
                if (e.key === "Escape") setNaming(null);
              }}
            />
            <SmallButton onClick={commitSave}>Save</SmallButton>
            <SmallButton variant="ghost" onClick={() => setNaming(null)}>
              Cancel
            </SmallButton>
          </>
        ) : match ? (
          <span>
            Saved as <InlineLink to={viewPath(match.id)}>{match.name} →</InlineLink>
          </span>
        ) : (
          hasFilters && (
            <>
              {editing && (
                <SmallButton
                  onClick={() => update(networkId, editing.id, { query })}
                  title={`Replace the filters saved in "${editing.name}" with the current ones`}
                >
                  Update “{editing.name}”
                </SmallButton>
              )}
              <SmallButton
                variant={editing ? "ghost" : undefined}
                onClick={() => setNaming(defaultViewName(filters))}
              >
                {editing ? "Save as new view" : "Save view"}
              </SmallButton>
            </>
          )
        )}
      </Actions>
    </Row>
  );
}
